import { Identifiable, RootState } from 'gf-types';
import { InitialsAspectRatio, SignatureAspectRatio } from '../formeditor/esignatureHelper';
import { FieldTypes, DisplayType, EditorTypes } from '../helpers/constants';
import { getIn } from '../helpers/helpers';
import {
  BasicElement,
  DataLayout,
  DataLayoutPosition,
  DataLayoutSection,
  DataLayoutTab,
  DefaultFieldDisplaySettings,
  FieldPosition,
  TableColumnPosition,
  TemplateColumn,
  TemplateField,
  TemplatePage
} from '../types/template-types';
import { KeyPath, TemplateDefaultSettings, TemplateFeature, TemplateSettings, TemplateState } from './templateeditor-types';
import { ProfileState } from '../profile/profile-types';
import { OnboardingState } from '../welcome/choosejourney/choose-journey-types';

export const getTemplateEditorState = ({ templateeditor }: RootState) => {
  return templateeditor.editor;
};

export const getTemplateState = ({ templateeditor }: RootState) => {
  return templateeditor.template.present;
};

export const getDataLayoutTabKeyPathById = (state: TemplateState, id: string): KeyPath => {
  const keyPath: KeyPath = ['template', 'dataLayouts'];
  const dataLayoutIndex = getIn<DataLayoutTab[]>(state, keyPath).findIndex((p) => {
    return p.id === id;
  });

  keyPath.push(dataLayoutIndex);

  return keyPath;
};

export const getItemIndexId = <T extends Identifiable>(collection: T[], id: string): number => {
  return collection.findIndex((p) => p.id === id);
};

export const getItemById = <T extends Identifiable>(collection: T[], id: string): T => {
  return collection.find((p) => p.id === id);
};

export const getItemKeyPathById = (parent: unknown, collectionName: string, id: string): KeyPath => {
  const keyPath: KeyPath = [collectionName];
  const dataLayoutIndex = getItemIndexId(parent[collectionName], id);

  keyPath.push(dataLayoutIndex);

  return keyPath;
};

export const getDataLayoutSectionKeyPathById = (state: TemplateState, dataLayoutId: string, dataLayoutTabId: string, dataLayoutSectionId: string): KeyPath => {
  const keyPath: KeyPath = ['template'];
  const template = state.template;

  keyPath.push('dataLayouts');
  keyPath.push(...getItemKeyPathById(template, 'dataLayouts', dataLayoutId));

  const dataLayout = getIn<DataLayout[]>(state, keyPath);

  keyPath.push('dataLayoutTabs');
  keyPath.push(...getItemKeyPathById(dataLayout, 'dataLayoutTabs', dataLayoutTabId));

  const dataLayoutTab = getIn<DataLayoutTab[]>(state, keyPath);

  keyPath.push('dataLayoutSections');
  keyPath.push(...getItemKeyPathById(dataLayoutTab, 'dataLayoutSections', dataLayoutSectionId));

  return keyPath;
};

export const getFieldKeyPathById = (state: TemplateState, fieldId: string) => {
  const keyPath: KeyPath = ['template', 'fields'];
  const fieldIndex = getIn<TemplateField[]>(state, keyPath).findIndex((f) => f.id === fieldId);

  if (fieldIndex === -1) {
    console.log(`couldn't find field ${fieldId}`);
    return null;
  }

  keyPath.push(fieldIndex);

  return keyPath;
};

export const getColumnKeyPath = (state: TemplateState, fieldId: string, columnId: string): KeyPath => {
  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const columnsKeyPath = [...fieldKeyPath, 'columns'];
  const columns = getIn<TemplateColumn[]>(state, columnsKeyPath);
  const columnIndex = columns.findIndex((x) => x.id === columnId);

  return [...columnsKeyPath, columnIndex];
};

export const getFieldPositionKeyPathById = (state: TemplateState, pageId: string, fieldPositionId: string): KeyPath => {
  const keyPath: KeyPath = ['template', 'pages'];
  const pageIndex = getIn<TemplatePage[]>(state, keyPath).findIndex((p) => {
    return p.id === pageId;
  });

  keyPath.push(pageIndex);

  const page = getIn<TemplatePage>(state, keyPath);

  keyPath.push('fieldPositions');

  const fieldPositionIndex = page.fieldPositions.findIndex((p) => {
    return p.id === fieldPositionId;
  });

  keyPath.push(fieldPositionIndex);

  return keyPath;
};

export const getFieldPosition = (state: TemplateState, pageId: string, fieldPositionId: string): FieldPosition => {
  const keyPath = getFieldPositionKeyPathById(state, pageId, fieldPositionId);

  return getIn<FieldPosition>(state, keyPath);
};

export const getAllFieldPositionsWithDefaultState = (state: TemplateState) => {
  const keyPathsResult: KeyPath[] = [];
  const keyPath = ['template', 'pages'];
  const pages = getIn<TemplatePage[]>(state, keyPath);

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex];
    const fieldPositions = page.fieldPositions;

    if (fieldPositions) {
      for (let fieldPositionIndex = 0; fieldPositionIndex < fieldPositions.length; fieldPositionIndex++) {
        const fieldPosition = fieldPositions[fieldPositionIndex];

        if (fieldPosition.useDefaultDisplaySettings == true) {
          keyPathsResult.push([...keyPath, ...[pageIndex, 'fieldPositions', fieldPositionIndex]]);
        }
      }
    }
  }

  return keyPathsResult;
};

export const getFieldPositionKeyPathsByFieldId = (state: TemplateState, fieldId: string) => {
  const keyPathsResult: KeyPath[] = [];
  const keyPath = ['template', 'pages'];
  const pages = getIn<TemplatePage[]>(state, keyPath);

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex];
    const fieldPositions = page.fieldPositions;

    if (fieldPositions) {
      for (let fieldPositionIndex = 0; fieldPositionIndex < fieldPositions.length; fieldPositionIndex++) {
        const fieldPosition = fieldPositions[fieldPositionIndex];

        if (fieldPosition.fieldId === fieldId) {
          keyPathsResult.push([...keyPath, ...[pageIndex, 'fieldPositions', fieldPositionIndex]]);
        }
      }
    }
  }

  return keyPathsResult;
};

/**
 * Determines if the template field overlay should show.
 *
 * @param state The template state.
 * @param profile The user's profile.
 */
export const showTemplateFieldOverlay = (state: TemplateState, profile: ProfileState, onboarding: OnboardingState): boolean => {
  // don't show when these conditions are met

  // they are dragging a field
  if (state.addFieldDragging) {
    return false;
  }

  // they already dragged a field onto a form somewhere
  if (onboarding.hasDroppedFirstField) {
    return false;
  }

  // they are no longer in a trial
  if (!profile.isInTrial) {
    return false;
  }

  // they have already a template field
  if (state.template?.fields?.length > 0) {
    return false;
  }

  // they're not viewing the field editor
  if (state.activeEditor !== EditorTypes.FieldEditor) {
    return false;
  }

  // they didn't meet any of the above requirements, so show the
  // overlay
  return true;
};

/**
 * Determines if an analytics event should be dispatched for a new, in-trial, user without
 * a field on any templates.
 *
 * @param profile The profile state.
 * @param onboarding The onbaroding state.
 */
export const shouldTrackFirstDroppedField = (profile: ProfileState, onboarding: OnboardingState) => {
  return profile.isInTrial && !onboarding.hasDroppedFirstField;
};

export const getColumnPositionKeyPathsByColumnId = (state: TemplateState, fieldId: string, columnId: string): KeyPath[] => {
  const keyPathsResult = [];
  const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);

  for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
    const columnPositions = getIn<TableColumnPosition[]>(state, [...fieldPositionKeyPath, 'columns']);

    if (columnPositions) {
      for (let columnPositionIndex = 0; columnPositionIndex < (columnPositions ? columnPositions.length : 0); columnPositionIndex++) {
        const columnPosition = columnPositions[columnPositionIndex];

        if (columnPosition.columnId === columnId) {
          keyPathsResult.push([...fieldPositionKeyPath, 'columns', columnPositionIndex]);
        }
      }
    }
  }

  return keyPathsResult;
};

export const getField = (state: TemplateState, fieldId: string): TemplateField => {
  const keyPath = getFieldKeyPathById(state, fieldId);

  if (!keyPath) {
    return null;
  }

  return getIn(state, keyPath);
};

export const getColumn = (state: TemplateState, fieldId: string, columnId: string): TemplateColumn => {
  const keyPath = getColumnKeyPath(state, fieldId, columnId);

  if (!keyPath) {
    return null;
  }

  return getIn(state, keyPath);
};

export const getSelectedPageIndex = (state: TemplateState): number => {
  const pageId = state.selection?.pageId;

  if (!pageId) {
    return null;
  }

  const pages = state.template.pages;

  return getItemIndexId(pages, pageId);
};

export const getSelectedFieldPositions = (state: TemplateState) => {
  return state.selection.fieldPositions?.map((keyPath) => getIn<FieldPosition>(state, keyPath)) || [];
};

export const getSelecteBasicElements = (state: TemplateState) => {
  return state.selection.elements?.map((keyPath) => getIn<BasicElement>(state, keyPath)) || [];
};

export const getFieldPositionKeyPathsByCheckBoxItemId = (state: TemplateState, checkBoxItemId: string) => {
  const positions: KeyPath[] = [];
  const pages = state.template.pages;

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const fieldPositions = pages[pageIndex].fieldPositions;

    if (fieldPositions) {
      for (let fieldPositionIndex = 0; fieldPositionIndex < fieldPositions.length; fieldPositionIndex++) {
        const fieldPosition = fieldPositions[fieldPositionIndex];

        if (fieldPosition && fieldPosition.itemId === checkBoxItemId) {
          positions.push(['template', 'pages', pageIndex, 'fieldPositions', fieldPositionIndex]);
        }
      }
    }
  }

  return positions;
};

export const getDataLayoutPositionKeyPathsByFieldId = (state: TemplateState, fieldId: string): KeyPath[] => {
  const keyPathsResult: KeyPath[] = [];
  const dataLayouts = state.template.dataLayouts;

  for (let dataLayoutIndex = 0; dataLayoutIndex < dataLayouts.length; dataLayoutIndex++) {
    const dataLayoutTabs = getIn<DataLayoutTab[]>(dataLayouts, [dataLayoutIndex, 'dataLayoutTabs']);

    if (dataLayoutTabs) {
      for (let dataLayoutTabIndex = 0; dataLayoutTabIndex < dataLayoutTabs.length; dataLayoutTabIndex++) {
        const dataLayoutSections = getIn<DataLayoutSection[]>(dataLayoutTabs, [dataLayoutTabIndex, 'sections']);

        if (dataLayoutSections) {
          for (let dataLayoutSectionIndex = 0; dataLayoutSectionIndex < dataLayoutSections.length; dataLayoutSectionIndex++) {
            const dataLayoutPositions = getIn<DataLayoutPosition[]>(dataLayoutSections, [dataLayoutSectionIndex, 'positions']);

            if (dataLayoutPositions) {
              for (let dataLayoutPositionIndex = 0; dataLayoutPositionIndex < dataLayoutPositions.length; dataLayoutPositionIndex++) {
                const dataLayoutPosition = dataLayoutPositions[dataLayoutPositionIndex];

                if (dataLayoutPosition.fieldId === fieldId) {
                  keyPathsResult.push([
                    'template',
                    'dataLayouts',
                    dataLayoutIndex,
                    'dataLayoutTabs',
                    dataLayoutTabIndex,
                    'sections',
                    dataLayoutSectionIndex,
                    'positions',
                    dataLayoutPositionIndex
                  ]);
                }
              }
            }
          }
        }
      }
    }
  }

  return keyPathsResult;
};

export const getDataLayoutPositionItemKeyPathsByCheckBoxItemId = (state: TemplateState, checkBoxItemId: string): KeyPath[] => {
  const positions: KeyPath[] = [];
  const dataLayouts = state.template.dataLayouts;

  for (let dataLayoutIndex = 0; dataLayoutIndex < dataLayouts.length; dataLayoutIndex++) {
    const dataLayoutTabs = dataLayouts[dataLayoutIndex].dataLayoutTabs;

    if (dataLayoutTabs) {
      for (let dataLayoutTabIndex = 0; dataLayoutTabIndex < dataLayoutTabs.length; dataLayoutTabIndex++) {
        const dataLayoutSections = dataLayoutTabs[dataLayoutTabIndex].sections;

        if (dataLayoutSections) {
          for (let dataLayoutSectionIndex = 0; dataLayoutSectionIndex < dataLayoutSections.length; dataLayoutSectionIndex++) {
            const dataLayoutPositions = dataLayoutSections[dataLayoutSectionIndex].positions;

            if (dataLayoutPositions) {
              for (let dataLayoutPositionIndex = 0; dataLayoutPositionIndex < dataLayoutPositions.length; dataLayoutPositionIndex++) {
                const dataLayoutPosition = dataLayoutPositions[dataLayoutPositionIndex];
                const itemIndex =
                  dataLayoutPosition && dataLayoutPosition.checkBoxGroupItems
                    ? dataLayoutPosition.checkBoxGroupItems.findIndex((x) => x.itemId === checkBoxItemId)
                    : -1;

                if (itemIndex > -1) {
                  positions.push([
                    'template',
                    'dataLayouts',
                    dataLayoutIndex,
                    'dataLayoutTabs',
                    dataLayoutTabIndex,
                    'sections',
                    dataLayoutSectionIndex,
                    'positions',
                    dataLayoutPositionIndex,
                    'checkBoxGroupItems',
                    itemIndex
                  ]);
                }
              }
            }
          }
        }
      }
    }
  }

  return positions;
};

export const getHighestTabIndex = (state: TemplateState) => {
  let highestTabIndex = -1;
  const pages = state.template.pages;

  for (const page of pages) {
    const fieldPositions = page.fieldPositions;

    if (fieldPositions) {
      const positions = fieldPositions.filter(({ tabIndex }) => tabIndex !== null && tabIndex !== undefined && tabIndex > -1);

      if (positions.length > 0) {
        const highestPageTabIndex = Math.max(...positions.map((p) => p.tabIndex));

        highestTabIndex = Math.max(highestTabIndex, highestPageTabIndex);
      }
    }
  }

  return highestTabIndex;
};

export function getDefaultSettingType(fieldType: number) {
  switch (fieldType) {
    case FieldTypes.Table:
      return 'table';
    case FieldTypes.Signature:
      return 'signature';
    case FieldTypes.eSignature:
      return 'esignsignature';
    case FieldTypes.eSignatureInitials:
      return 'esigninitials';
    case FieldTypes.FileAttachment:
      return 'fileattachment';
    case FieldTypes.Sketch:
    case FieldTypes.Image:
      return 'media';
    case FieldTypes.CheckBox:
    case FieldTypes.CheckBoxGroup:
      return 'checkbox';
    case FieldTypes.GPS:
      return 'gps';
    default:
      return 'default';
  }
}

export function getFieldDefaultSettings(defaultSettings: TemplateDefaultSettings, fieldType: number, features: TemplateFeature) {
  const settings: TemplateSettings = {
    maxFileSize: features.maxFileSize || 10,
    maxFileCount: features.maxFileCount || 10,
    audioMaxLength: features.audioMaxLength || 10,
    allowedFileTypes: ''
  };

  if (fieldType === FieldTypes.Sketch || fieldType === FieldTypes.Image || fieldType === FieldTypes.GPS) {
    settings.color = '#000000';
    settings.fontSize = 14;
    settings.fontName = 'Helvetica';
  }

  const defaultSettingType = getDefaultSettingType(fieldType);
  const specificDefaultSettings: TemplateSettings = defaultSettings ? defaultSettings[defaultSettingType] : null;

  if (specificDefaultSettings) {
    if (specificDefaultSettings.maxFileSize) {
      settings.maxFileSize = specificDefaultSettings.maxFileSize;
    }
    if (specificDefaultSettings.maxFileCount) {
      settings.maxFileCount = specificDefaultSettings.maxFileCount;
    }
    if (specificDefaultSettings.allowedFileTypes) {
      settings.allowedFileTypes = specificDefaultSettings.allowedFileTypes;
    }
  }

  return settings;
}

export const GetDefaultTemplateSettings = (defaultFieldDisplaySettings: DefaultFieldDisplaySettings) => {
  return {
    width: 200,
    height: 40,
    backgroundColor: defaultFieldDisplaySettings?.backgroundColor || '#D8F5FF',
    color: defaultFieldDisplaySettings?.color || '#000000',
    focusColor: defaultFieldDisplaySettings?.focusColor || '#C0FFFF',
    fontSize: defaultFieldDisplaySettings?.fontSize || 14,
    fontName: defaultFieldDisplaySettings?.fontName || 'Arial',
    align: defaultFieldDisplaySettings?.align || 'Left',
    displayType: undefined
  };
};

export function getFieldPositionDefaultSettings(
  defaultSettings: TemplateDefaultSettings,
  fieldType: number,
  defaultFieldDisplaySettings: DefaultFieldDisplaySettings
): TemplateSettings {
  const settings = GetDefaultTemplateSettings(defaultFieldDisplaySettings);

  if (fieldType === FieldTypes.CheckBox || fieldType === FieldTypes.CheckBoxGroup) {
    settings.width = 40;
    settings.height = 40;
    settings.displayType = DisplayType.Xmark;
  } else if (fieldType === FieldTypes.Table) {
    settings.width = 220;
    settings.height = 100;
  } else if (fieldType === FieldTypes.Signature) {
    settings.width = 400;
    settings.height = 110;
    settings.color = '#000000';
  } else if (fieldType === FieldTypes.eSignature) {
    settings.width = Math.round(110 * SignatureAspectRatio);
    settings.height = 110;
    settings.color = '#000000';
  } else if (fieldType === FieldTypes.eSignatureInitials) {
    settings.width = Math.round(80 * InitialsAspectRatio);
    settings.height = 80;
    settings.color = '#000000';
  } else if (fieldType === FieldTypes.Image) {
    settings.width = 300;
    settings.height = 300;
    settings.color = '#000000';
    settings.fontSize = 14;
    settings.fontName = 'Helvetica';
    settings.align = 'Top Left';
  } else if (fieldType === FieldTypes.Sketch) {
    settings.width = 300;
    settings.height = 300;
    settings.color = '#000000';
    settings.fontSize = 14;
    settings.fontName = 'Helvetica';
  } else if (fieldType === FieldTypes.GPS) {
    settings.width = 300;
    settings.height = 300;
    settings.displayType = DisplayType.GPSMap;
  }

  const defaultSettingType = getDefaultSettingType(fieldType);
  const specificDefaultSettings = defaultSettings ? defaultSettings[defaultSettingType] : null;

  if (specificDefaultSettings) {
    if (specificDefaultSettings.w) {
      settings.width = specificDefaultSettings.w;
    }
    if (specificDefaultSettings.h) {
      settings.height = specificDefaultSettings.h;
    }
    if (specificDefaultSettings.displayType) {
      settings.displayType = specificDefaultSettings.displayType;
    }
  }

  return settings;
}

export function getFieldMappedStatus(state: TemplateState, fieldId: string) {
  const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);
  const dataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, fieldId);

  return fieldPositionKeyPaths.length == 0 && dataLayoutPositionKeyPaths.length == 0 ? 'unmapped' : 'mapped';
}
